import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/system';
import { ReactNode } from 'react';

import { HBButton, HBButtonProps } from '../HBButton/HBButton';
import { HBIcon, HBIconType } from '../HBIcon/HBIcon';
import { HBDialogRootStyle } from './HBDialog.styles';

export interface HBDialogProps extends Omit<DialogProps, 'ref' | 'title' | 'content'> {
  title?: string | ReactNode;
  rejectBtn?: string;
  acceptBtn?: string;
  onAccept?: () => void;
  loadingProgress?: number;
  onReject?: () => void;
  hideCloseButton?: boolean;
  onAcceptBtnProps?: Omit<HBButtonProps, 'children' | 'onClick'>;
  loading?: boolean;
  contentSx?: SxProps<Theme>;
  rejectBtnSx?: SxProps;
  acceptBtnSx?: SxProps;
  iconType?: HBIconType;
  disabled?: boolean;
  content?: string | ReactNode;
}

export const HBDialog = ({
  children,
  content,
  loadingProgress,
  rejectBtn,
  acceptBtn,
  contentSx,
  onAcceptBtnProps,
  title,
  hideCloseButton,
  loading,
  onClose,
  onAccept,
  rejectBtnSx,
  acceptBtnSx,
  onReject,
  disabled = false,
  iconType = 'times',
  ...props
}: HBDialogProps): JSX.Element => {
  return (
    <HBDialogRootStyle {...props} onBackdropClick={(e) => onClose?.(e, 'backdropClick')}>
      {(title || !hideCloseButton) && (
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4,
              bgcolor: 'background.paper',
              borderRadius: 1,
            }}
            className="HBDialogTitle"
          >
            {title && typeof title === 'string' && <Typography variant="h6">{title}</Typography>}
            {title && typeof title !== 'string' && title}

            {!hideCloseButton && (
              <IconButton onClick={(e) => onClose?.(e, 'backdropClick')}>
                <HBIcon type={iconType} size="small" sx={{ color: 'grey.900', lineHeight: 0 }} />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
      )}
      <DialogContent
        sx={{
          ...contentSx,
        }}
      >
        {content && (
          <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
            {content}
          </Typography>
        )}
        {children}
      </DialogContent>
      {(rejectBtn || acceptBtn) && (
        <DialogActions>
          <Stack
            direction="row"
            gap={4}
            width="100%"
            justifyContent="space-between"
            alignItems="center"
          >
            {rejectBtn && (
              <HBButton sx={rejectBtnSx} variant="outlined" onClick={onReject}>
                {rejectBtn}
              </HBButton>
            )}
            {acceptBtn && (
              <HBButton
                sx={acceptBtnSx}
                disabled={loading || disabled}
                {...onAcceptBtnProps}
                onClick={onAccept}
              >
                {loading ? (
                  <CircularProgress
                    size={25}
                    variant={loadingProgress ? 'determinate' : 'indeterminate'}
                    value={loadingProgress}
                  />
                ) : (
                  acceptBtn
                )}
              </HBButton>
            )}
          </Stack>
        </DialogActions>
      )}
    </HBDialogRootStyle>
  );
};

HBDialog.displayName = 'HBDialog';
HBDialog.defaultProps = { hideCloseButton: false };
